import React from 'react';
import {FormattedMessage, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ScrollableFeed from 'react-scrollable-feed';

import Box from '../box/box.jsx';
import MenuBarMenu from '../menu-bar/menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import styles from './hardware-console.css';
import cleanIcon from './clean.svg';
import settingIcon from './setting.svg';
import pauseIcon from './pause.svg';
import startIcon from './start.svg';
import FlashLoadingOverlay from './FlashLoadingOverlay.jsx';

const toHexForm = buffer => Array.prototype.map.call(buffer,
    x => x.toString(16).toUpperCase()).join(' ');

const HardwareConsoleComponent = props => {
    const {
        baudrate,
        baudrateList,
        consoleArray,
        eol,
        eolList,
        intl,
        isAutoScroll,
        isHexForm,
        isPause,
        isPlaying,
        deviceId,
        isScanning,
        scannedDevices,
        scanTimeLeft,
        onClickPlay,
        onClickClean,
        onClickPause,
        onClickScan,
        onClickSerialportMenu,
        onClickHexForm,
        onClickAutoScroll,
        onClickSend,
        onInputChange,
        onKeyPress,
        onKeyDown,
        onRequestSerialportMenu,
        onSelectBaudrate,
        onSelectEol,
        serialportMenuOpen
    } = props;
    
    const isDualConsole = deviceId === 'arduinoEsp32S3';
    
    const MainConsole = () => (
        <Box className={classNames(styles.hardwareConsoleWrapper, {
            [styles.halfSize]: isDualConsole
        })}>
            <Box className={styles.consoleArray}>
                <ScrollableFeed forceScroll={isAutoScroll}>
                    <span>
                        {isHexForm ? toHexForm(consoleArray) : new TextDecoder('utf-8').decode(consoleArray)}
                    </span>
                </ScrollableFeed>
            </Box>
            <button
                className={classNames(styles.button, styles.playButton)}
                onClick={onClickPlay}
                style={{ display: deviceId === 'arduinoEsp32S3' ? 'block' : 'none' }}
            >
                {isPlaying ? 'Stop' : 'Play'}
            </button>
            <button
                className={classNames(styles.button, styles.pauseButton)}
                onClick={onClickPause}
            >
                <img
                    alt="Pause"
                    className={classNames(styles.pauseIcon)}
                    src={isPause ? startIcon : pauseIcon}
                />
            </button>
            <button
                className={classNames(styles.button, styles.cleanButton)}
                onClick={onClickClean}
            >
                <img
                    alt="Clean"
                    className={classNames(styles.cleanIcon)}
                    src={cleanIcon}
                />
            </button>
            <Box className={styles.consoleMenuWarpper}>
                <input
                    className={styles.consoleInput}
                    onChange={onInputChange}
                    onKeyPress={onKeyPress}
                    onKeyDown={onKeyDown}
                />
                <button
                    className={classNames(styles.button, styles.sendButton)}
                    onClick={onClickSend}
                >
                    <FormattedMessage
                        defaultMessage="Send"
                        description="Button in bottom to send data to serialport"
                        id="gui.hardwareConsole.send"
                    />
                </button>
                <button
                    className={classNames(styles.button, styles.settingButton)}
                >
                    <img
                        alt="Setting"
                        className={classNames(styles.settingIcon, {
                            [styles.active]: serialportMenuOpen
                        })}
                        src={settingIcon}
                        onMouseUp={onClickSerialportMenu}
                    />
                    <MenuBarMenu
                        className={classNames(styles.MenuBarMenu)}
                        menuClassName={styles.menu}
                        open={serialportMenuOpen}
                        place={'left'}
                        directiron={'up'}
                        onRequestClose={onRequestSerialportMenu}
                    >
                        <MenuSection>
                            <MenuItem isRtl={props.isRtl}>
                                <FormattedMessage
                                    defaultMessage="Buadrate"
                                    description="Serial buadrate."
                                    id="gui.hardwareConsole.buadrate"
                                />
                                <select onChange={onSelectBaudrate}>
                                    {baudrateList.map(item => (
                                        <option
                                            key={item.key}
                                            selected={baudrate === item.key}
                                        >
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </MenuItem>
                            <MenuItem isRtl={props.isRtl}>
                                <FormattedMessage
                                    defaultMessage="End of line"
                                    description="End of line."
                                    id="gui.hardwareConsole.endOfLine"
                                />
                                <select onChange={onSelectEol}>
                                    {eolList.map(item => (
                                        <option
                                            key={item.key}
                                            selected={eol === item.key}
                                        >
                                            {intl.formatMessage(item.value)}
                                        </option>
                                    ))}
                                </select>
                            </MenuItem>
                        </MenuSection>
                        <MenuSection>
                            <MenuItem onClick={onClickHexForm} isRtl={props.isRtl}>
                                <FormattedMessage
                                    defaultMessage="Hex form"
                                    description="Display serial port data in hexadecimal."
                                    id="gui.hardwareConsole.hexform"
                                />
                                <input
                                    type="checkbox"
                                    name="hexform"
                                    checked={isHexForm}
                                    readOnly
                                />
                            </MenuItem>
                            <MenuItem onClick={onClickAutoScroll} isRtl={props.isRtl} bottomLine>
                                <FormattedMessage
                                    defaultMessage="Auto scroll"
                                    description="Auto scroll serialport console data."
                                    id="gui.hardwareConsole.autoScroll"
                                />
                                <input
                                    type="checkbox"
                                    name="autoScroll"
                                    checked={isAutoScroll}
                                    readOnly
                                />
                            </MenuItem>
                        </MenuSection>
                    </MenuBarMenu>
                </button>
            </Box>
        </Box>
    );
    
    const DeviceScanner = () => (
        <Box className={classNames(styles.hardwareConsoleWrapper, styles.halfSize, styles.deviceScanner)} style={{position: 'relative'}}>
            <FlashLoadingOverlay 
                isFlashing={props.isFlashing} 
                flashProgress={props.flashProgress} 
            />
            <div className={styles.scannerHeader}>
                <h3>Quét thiết bị</h3>
                <button
                    className={classNames(styles.button, styles.scanButton)}
                    onClick={onClickScan}
                    disabled={props.isFlashing}
                >
                    {isScanning ? `Stop (${scanTimeLeft}s)` : 'Scan'}
                </button>
            </div>
            <Box className={styles.consoleArray}>
                {scannedDevices.length === 0 ? (
                    <div className={styles.emptyState}>
                        {isScanning 
                            ? `Đang quét... (${scanTimeLeft}s còn lại)` 
                            : 'Nhấn vào Scan để quét'}
                    </div>
                ) : (
                    <div className={styles.deviceList}>
                        {scannedDevices.filter(device => device.found).map(device => (
                            <div 
                                key={device.port}
                                className={classNames(styles.deviceItem, {
                                    [styles.deviceFound]: device.found,
                                    [styles.deviceNotFound]: !device.found
                                })}
                            >
                                <div className={styles.deviceHeader}>
                                    <span className={styles.portLabel}>Port {device.port}</span>
                                    <span className={classNames(styles.statusBadge, {
                                        [styles.found]: device.found
                                    })}>
                                        {device.commtype === "A/D" ? `digital : ${device.digital} , analog : ${device.analog} , device : ${device.device_name}` : `channel : ${device.channel} , device : ${device.device_name} , bus : ${device.bus}`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Box>
        </Box>
    );
    
    return isDualConsole ? (
    <Box className={styles.dualConsoleContainer}>
        <MainConsole />
        <Box className={styles.divider} />
        <DeviceScanner />
    </Box>
    ) : (
        <MainConsole />
    );
};

HardwareConsoleComponent.propTypes = {
    baudrate: PropTypes.string.isRequired,
    baudrateList: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.number.isRequired
        })),
    consoleArray: PropTypes.instanceOf(Uint8Array),
    eol: PropTypes.string.isRequired,
    eolList: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.shape({
                defaultMessage: PropTypes.string.isRequired,
                description: PropTypes.string,
                id: PropTypes.string.isRequired
            })
        })),
    intl: intlShape,
    isRtl: PropTypes.bool,
    isHexForm: PropTypes.bool.isRequired,
    isPause: PropTypes.bool.isRequired,
    isPlaying: PropTypes.bool,
    isScanning: PropTypes.bool,
    deviceId: PropTypes.string,
    isAutoScroll: PropTypes.bool.isRequired,
    scanTimeLeft: PropTypes.number,
    scannedDevices: PropTypes.arrayOf(PropTypes.shape({
        port: PropTypes.number,
        commtype: PropTypes.string,
        bus: PropTypes.number,
        channel: PropTypes.number,
        found: PropTypes.bool,
        i2c_address: PropTypes.number
    })),
    onClickClean: PropTypes.func.isRequired,
    onClickPlay: PropTypes.func,
    onClickPause: PropTypes.func.isRequired,
    onClickScan: PropTypes.func,
    onClickAutoScroll: PropTypes.func.isRequired,
    onClickHexForm: PropTypes.func.isRequired,
    onClickSend: PropTypes.func.isRequired,
    onClickSerialportMenu: PropTypes.func.isRequired,
    onInputChange: PropTypes.func.isRequired,
    onKeyPress: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    onRequestSerialportMenu: PropTypes.func.isRequired,
    onSelectBaudrate: PropTypes.func.isRequired,
    onSelectEol: PropTypes.func.isRequired,
    serialportMenuOpen: PropTypes.bool.isRequired
};

HardwareConsoleComponent.defaultProps = {
    isScanning: false,
    scannedDevices: [],
    scanTimeLeft: 10
};

export default HardwareConsoleComponent;